import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchPaymentMethods,
  fetchPaymentMethod,
  createPaymentMethod,
  updatePaymentMethod,
} from "@/api/payments";
import { PaymentMethod } from "@/types";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Save, ShieldAlert } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const PaymentMethodForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);

  const isNewMethod = !id || id === "new";
  const title = isNewMethod ? "Add Payment Method" : "Edit Payment Method";
  const methodId = isNewMethod ? null : parseInt(id!, 10);

  // Check if current user has admin privileges
  const hasAdminPrivileges = useCallback((): boolean => {
    if (!currentUser || !currentUser.roles) return false;
    return currentUser.roles.some((role) => role.isSystem === true);
  }, [currentUser]);

  // Check for admin permission and show dialog if needed
  useEffect(() => {
    if (!hasAdminPrivileges()) {
      setPermissionDialogOpen(true);
    }
  }, [hasAdminPrivileges]);

  // Fetch all payment methods (for validation of duplicate names)
  const { data: paymentMethods = [] } = useQuery({
    queryKey: ["paymentMethods"],
    queryFn: fetchPaymentMethods,
  });

  // Default payment method state
  const defaultMethod: Omit<PaymentMethod, "id" | "createdAt" | "updatedAt"> = {
    name: "",
    description: "",
    platformFee: 0,
    feeType: "percentage",
    supportedCurrencies: [],
    isDefault: false,
    isActive: true,
  };

  // Form state
  const [method, setMethod] = useState<typeof defaultMethod>(defaultMethod);
  const [errors, setErrors] = useState({
    name: "",
    platformFee: "",
    supportedCurrencies: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  // If editing, fetch current payment method data
  const { data: existingMethod, isLoading } = useQuery({
    queryKey: ["paymentMethod", methodId],
    queryFn: () => (methodId ? fetchPaymentMethod(methodId) : null),
    enabled: !!methodId,
  });

  useEffect(() => {
    if (existingMethod) {
      setMethod({
        name: existingMethod.name,
        description: existingMethod.description || "",
        platformFee: existingMethod.platformFee,
        feeType: existingMethod.feeType,
        supportedCurrencies: existingMethod.supportedCurrencies,
        isDefault: existingMethod.isDefault,
        isActive: existingMethod.isActive,
      });
    }
  }, [existingMethod]);

  // Handle input changes
  const handleInputChange = (
    field: keyof typeof defaultMethod,
    value: string | number | boolean | string[]
  ) => {
    setMethod((prev) => ({ ...prev, [field]: value }));

    // Clear errors when user types
    if (field in errors) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors = {
      name: "",
      platformFee: "",
      supportedCurrencies: "",
    };
    let isValid = true;

    if (!method.name.trim()) {
      newErrors.name = "Payment method name is required";
      isValid = false;
    } else if (
      paymentMethods.some(
        (m) =>
          m.name.toLowerCase() === method.name.toLowerCase() &&
          m.id !== methodId
      )
    ) {
      newErrors.name = "A payment method with this name already exists";
      isValid = false;
    }

    if (method.platformFee < 0) {
      newErrors.platformFee = "Platform fee cannot be negative";
      isValid = false;
    }

    if (method.supportedCurrencies.length === 0) {
      newErrors.supportedCurrencies = "At least one currency must be supported";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      if (isNewMethod) {
        // Create new payment method
        await createPaymentMethod(method);
        toast({
          title: "Payment method created",
          description: `${method.name} has been created successfully.`,
        });
      } else if (methodId) {
        // Update existing payment method
        await updatePaymentMethod(methodId, method);
        toast({
          title: "Payment method updated",
          description: `${method.name} has been updated successfully.`,
        });
      }

      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["paymentMethods"] });

      // Redirect back to payment methods list
      navigate("/admin/system/payment-methods");
    } catch (error) {
      console.error("Error saving payment method:", error);

      // Show error message
      let errorMessage = "An error occurred while saving the payment method";
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout title={title}>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={title}>
      <div className="mb-6">
        <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
          Back to Payment Methods
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>
            {isNewMethod
              ? "Create a new payment method for the system."
              : "Update the details of an existing payment method."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Method Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  value={method.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className={errors.name ? "border-destructive" : ""}
                  placeholder="e.g. Stripe, PayPal, Apple Pay"
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={method.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  placeholder="Brief description of the payment method"
                  className="max-w-2xl"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="feeType">
                    Fee Type <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={method.feeType}
                    onValueChange={(value: "percentage" | "fixed") =>
                      handleInputChange("feeType", value)
                    }
                  >
                    <SelectTrigger id="feeType">
                      <SelectValue placeholder="Select fee type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="platformFee">
                    Platform Fee{" "}
                    {method.feeType === "percentage" ? "(%)" : "($)"}{" "}
                    <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="platformFee"
                    type="number"
                    min="0"
                    step={method.feeType === "percentage" ? "0.1" : "0.01"}
                    value={method.platformFee}
                    onChange={(e) =>
                      handleInputChange(
                        "platformFee",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    className={errors.platformFee ? "border-destructive" : ""}
                    placeholder={
                      method.feeType === "percentage"
                        ? "Enter percentage"
                        : "Enter amount"
                    }
                  />
                  {errors.platformFee && (
                    <p className="text-sm text-destructive">
                      {errors.platformFee}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="supportedCurrencies">
                    Supported Currencies{" "}
                    <span className="text-destructive">*</span>
                  </Label>
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {method.supportedCurrencies.map((currency) => (
                        <div
                          key={currency}
                          className="flex items-center gap-1 bg-muted px-2 py-1 rounded-md text-sm"
                        >
                          <span>{currency}</span>
                          <button
                            type="button"
                            onClick={() =>
                              handleInputChange(
                                "supportedCurrencies",
                                method.supportedCurrencies.filter(
                                  (c) => c !== currency
                                )
                              )
                            }
                            className="text-muted-foreground hover:text-destructive"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    <Select
                      value=""
                      onValueChange={(value) => {
                        if (
                          value &&
                          !method.supportedCurrencies.includes(value)
                        ) {
                          handleInputChange("supportedCurrencies", [
                            ...method.supportedCurrencies,
                            value,
                          ]);
                        }
                      }}
                    >
                      <SelectTrigger
                        id="supportedCurrencies"
                        className={
                          errors.supportedCurrencies ? "border-destructive" : ""
                        }
                      >
                        <SelectValue placeholder="Add currency" />
                      </SelectTrigger>
                      <SelectContent>
                        {["USD", "EUR", "GBP", "INR", "MMK"]
                          .filter(
                            (currency) =>
                              !method.supportedCurrencies.includes(currency)
                          )
                          .map((currency) => (
                            <SelectItem key={currency} value={currency}>
                              {currency}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {errors.supportedCurrencies && (
                    <p className="text-sm text-destructive">
                      {errors.supportedCurrencies}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-8">
                <div className="flex items-center gap-2">
                  <Switch
                    id="isDefault"
                    checked={method.isDefault}
                    onCheckedChange={(checked) =>
                      handleInputChange("isDefault", checked)
                    }
                  />
                  <Label htmlFor="isDefault">Default Payment Method</Label>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    id="isActive"
                    checked={method.isActive}
                    onCheckedChange={(checked) =>
                      handleInputChange("isActive", checked)
                    }
                  />
                  <Label htmlFor="isActive">Active</Label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving} className="gap-2">
                {isSaving && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                )}
                <Save className="h-4 w-4" />
                Save Method
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Permission Dialog */}
      <Dialog
        open={permissionDialogOpen}
        onOpenChange={(open) => {
          setPermissionDialogOpen(open);
          if (!open) {
            navigate("/admin/system/payment-methods");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              Permission Required
            </DialogTitle>
            <DialogDescription>
              You need administrator privileges to manage payment methods. This
              action is restricted to system administrators only.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => {
                setPermissionDialogOpen(false);
                navigate("/admin/system/payment-methods");
              }}
            >
              Return to Payment Methods
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default PaymentMethodForm;
